import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    document.body.style.overflow = '';
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = [
    { label: 'Home', href: '/art-classes' },
    { label: 'Services', href: '/parties-events' },
    { label: 'About Us', href: '/about' },
    { label: 'Location', href: '/location' },
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-artden-black/98 backdrop-blur-md py-3 border-b border-white/5'
            : 'bg-transparent py-5'
        }`}
      >
        {/* Paint accent line top */}
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, #ff4322 0%, #ffb300 35%, #ff7120 70%, #e0285a 100%)' }} />

        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center shrink-0 z-10">
            <img
              src="https://artdenstudio.com/wp-content/uploads/2024/11/logo.png"
              alt="Art Den Studio — Art Classes & Parties in Nazareth PA"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-outfit text-sm font-medium tracking-wide transition-all whitespace-nowrap relative group ${
                  isActive(link.href) ? 'text-artden-coral' : 'text-white/75 hover:text-white'
                }`}
              >
                {link.label}
                <span className={`absolute -bottom-0.5 left-0 h-px bg-artden-coral transition-all duration-300 ${isActive(link.href) ? 'w-full' : 'w-0 group-hover:w-full'}`} />
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:6103400314"
              className="flex items-center gap-1.5 text-white/50 hover:text-white text-sm font-medium transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-artden-coral"></i>
              </span>
              610-340-0314
            </a>
            <a
              href="https://artdenstudio.com/calendar/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-artden-coral hover:brightness-110 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all whitespace-nowrap"
            >
              Book a Class
            </a>
          </div>

          <button
            className="md:hidden text-white p-1 z-10"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <span className="w-7 h-7 flex items-center justify-center">
              <i className={mobileOpen ? 'ri-close-line text-2xl' : 'ri-menu-3-line text-2xl'}></i>
            </span>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 flex flex-col transition-all duration-500 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: '#080808' }}
      >
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, #ff4322 0%, #ffb300 35%, #ff7120 70%, #e0285a 100%)' }} />
        <div className="pt-28 px-8 flex flex-col gap-1">
          {navLinks.map((link, i) => (
            <Link
              key={link.href}
              to={link.href}
              className={`font-cormorant text-4xl font-medium border-b border-white/8 py-4 transition-all duration-300 ${
                mobileOpen ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'
              } ${isActive(link.href) ? 'text-artden-coral' : 'text-white hover:text-artden-coral'}`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              {link.label}
            </Link>
          ))}
          <div className="flex flex-col gap-3 pt-8">
            <a
              href="https://artdenstudio.com/calendar/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-artden-coral text-white text-center py-4 rounded-full font-semibold text-lg"
            >
              Book a Class
            </a>
            <a
              href="tel:6103400314"
              className="border border-white/15 text-white text-center py-4 rounded-full font-medium text-base"
            >
              Call 610-340-0314
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
